import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import moment from 'moment';
import { ErpOrder } from '../../models/ErpOrder';
import { ErpNfe } from '../../models/ErpNfe';
import { ShippingAddress } from '../../models/ShippingAddress';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../common-components/Button/Button';
import { DropdownButton, DropdownButtonAlignment, DropdownButtonColor, DropdownButtonDirection, IDropdownButtonItem } from '../../common-components/DropdownButton/DropdownButton';
import { Field, FieldType } from '../../common-components/Field/Field';
import { WaitingBox } from '../../common-components/WaitingBox/WaitingBox';
import { GetErpOrderNFeInput } from '../../services/Orders/GetErpOrderNFe/GetErpOrderNFeInput';
import { HistoryByOrder } from '../../services/Orders/GetHistoryByOrder/HistoryByOrder';
import { SaveErpOrderNFeDestinationAddressInput, SaveErpOrderNFeInput } from '../../services/Orders/SaveErpOrderNFe/SaveErpOrderNFeInput';
import { ImportErpOrderNFeXmlInput } from '../../services/Orders/ImportErpOrderNFeXml/ImportErpOrderNFeXmlInput';
import { ImportErpOrderNFeXmlOutput } from '../../services/Orders/ImportErpOrderNFeXml/ImportErpOrderNFeXmlOutput';
import { ConfirmImportedErpOrderNFeInput } from '../../services/Orders/ConfirmImportedErpOrderNFe/ConfirmImportedErpOrderNFeInput';
import { federativeUnits } from '../../services/FederativeUnits/FederativeUnitsService';
import { NfBlock, Title, CreatedAt, Status, LeftContainer, RightContainer, Details, FooterContent, ListItem, HistoryBlock } from './styles';
import { zerezesErpCode } from '../../resources/erpResources';
import { strings } from '../../resources/strings';
import { ProfileType } from '../../enums/ProfileType';

interface IModalListNFesProps {
  visible: boolean;
  close: (orderId?: number) => void;
  title: string;
  orderId?: number;
}

const ModalListNFes: React.FC<IModalListNFesProps> = ({
  visible,
  close,
  title,
  orderId,
}) => {
  const nfeActionKey = {
    View: 'view',
    Edit: 'edit',
    Cancel: 'cancel',
    Delete: 'delete',
  };
  const federativeUnitOptions = [{ abbreviation: '', name: '' }, ...federativeUnits];
  const xmlFileInput = React.useRef<HTMLInputElement | null>(null);
  const initialManualNFeForm = {
    nfeNumber: '',
    series: '',
    totalValue: '',
    keyNfe: '',
    authorizationProtocol: '',
    issuedAt: null as Date | null,
    destinationPublicPlace: '',
    destinationNumber: '',
    destinationDistrict: '',
    destinationCityName: '',
    destinationState: '',
    destinationZipCode: '',
    destinationAdditionalAddress: '',
    destinationDocumentNumber: '',
  };

  const { session } = useAuth();
  const isAdmin = session?.user?.profileType === ProfileType.Admin;
  const isEcommerce = session?.user?.profileType === ProfileType.Ecommerce;
  const canAccessNFeActions = isAdmin || isEcommerce;
  const [erpOrder, setErpOrder] = React.useState<ErpOrder | undefined | null>(undefined);
  const [formNFeVisible, setFormNFeVisible] = React.useState<boolean>(false);
  const [manualNFeFormVisible, setManualNFeFormVisible] = React.useState<boolean>(false);
  const [importXmlViewVisible, setImportXmlViewVisible] = React.useState<boolean>(false);
  const [importXmlConfirmationVisible, setImportXmlConfirmationVisible] = React.useState<boolean>(false);
  const [importedNFeViewingVisible, setImportedNFeViewingVisible] = React.useState<boolean>(false);
  const [editingManualNFe, setEditingManualNFe] = React.useState<ErpNfe | null>(null);
  const [numberNF, setNumberNF] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(true);
  const [importingXml, setImportingXml] = React.useState<boolean>(false);
  const [confirmingImportedXml, setConfirmingImportedXml] = React.useState<boolean>(false);
  const [importXmlOutput, setImportXmlOutput] = React.useState<ImportErpOrderNFeXmlOutput | null>(null);
  const [history, setHistory] = React.useState<HistoryByOrder[]>([]);
  const [currentErpIsProtheus, setCurrentErpIsProtheus] = React.useState<boolean>(false);
  const [shippingAddress, setShippingAddress] = React.useState<ShippingAddress | null>(null);
  const [manualNFeForm, setManualNFeForm] = React.useState(initialManualNFeForm);

  const {
    toastService,
    alertService,
    getUpdatedErpOrderService,
    getErpNfService,
    cancelErpNfeService,
    deleteErpOrderNFeService,
    getErpOrderNFeService,
    saveErpOrderNFeService,
    importErpOrderNFeXmlService,
    confirmImportedErpOrderNFeService,
    getHistoryByOrderService,
    featureFlagService,
    getShippingAddressByOrderService,
    getOrderAddressesService,
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    if (!visible) return;
    getErpOrder();
    getCurrentErpCodeFromAppConfig();
    getShippingAddress();
  }, [visible]);

  const body = React.useMemo(() => {
    if (!visible) return null;

    if (loading) return <WaitingBox />

    const showImportXmlDetails = currentErpIsProtheus && (importXmlConfirmationVisible || importedNFeViewingVisible);

    if (currentErpIsProtheus && manualNFeFormVisible) {
      return (
        <>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Field
              type={FieldType.Text}
              label={'Número da NFe'}
              value={manualNFeForm.nfeNumber}
              change={(value) => setManualNFeForm(current => ({ ...current, nfeNumber: value }))}
              marginBottom='16px'
              marginTop='0px'
              width='calc(50% - 8px)'
              widthMobile='100%'
              maxLength={20}
            />
            <Field
              type={FieldType.Text}
              label={'Série da NFe'}
              value={manualNFeForm.series}
              change={(value) => setManualNFeForm(current => ({ ...current, series: value }))}
              marginBottom='16px'
              marginTop='0px'
              width='calc(50% - 8px)'
              widthMobile='100%'
              maxLength={5}
            />
          </div>
            <Field
              type={FieldType.Text}
              label={'Chave da NFe'}
              value={manualNFeForm.keyNfe}
              change={(value) => setManualNFeForm(current => ({ ...current, keyNfe: value }))}
              marginBottom='16px'
              marginTop='0px'
              maxLength={44}
            />
            <Field
              type={FieldType.Text}
              label={'Protocolo de autorização'}
              value={manualNFeForm.authorizationProtocol}
              change={(value) => setManualNFeForm(current => ({ ...current, authorizationProtocol: value }))}
              marginBottom='16px'
              marginTop='0px'
              maxLength={30}
            />
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Field
              type={FieldType.DatePicker}
              label={'Data da NFe'}
              value={manualNFeForm.issuedAt}
              change={(value) => setManualNFeForm(current => ({ ...current, issuedAt: value }))}
              marginTop='0px'
              width='calc(50% - 8px)'
              widthMobile='100%'
            />
            <Field
              type={FieldType.Number}
              label={'Total da NFe'}
              value={manualNFeForm.totalValue}
              change={(value) => setManualNFeForm(current => ({ ...current, totalValue: value }))}
              marginTop='0px'
              width='calc(50% - 8px)'
              widthMobile='100%'
            />
          </div>
          <div style={{ marginTop: '8px', paddingTop: '12px', borderTop: '1px solid #e5e5e5' }}>
            <Field
              type={FieldType.Text}
              label={'Logradouro do endereço de entrega'}
              value={manualNFeForm.destinationPublicPlace}
              change={(value) => setManualNFeForm(current => ({ ...current, destinationPublicPlace: value }))}
              marginTop='0px'
              marginBottom='16px'
              maxLength={300}
            />
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <Field
                type={FieldType.Text}
                label={'Número do logradouro'}
                value={manualNFeForm.destinationNumber}
                change={(value) => setManualNFeForm(current => ({ ...current, destinationNumber: value }))}
                marginTop='0px'
                marginBottom='16px'
                width='calc(30% - 8px)'
                widthMobile='100%'
                maxLength={10}
              />
              <Field
                type={FieldType.Text}
                label={'Bairro'}
                value={manualNFeForm.destinationDistrict}
                change={(value) => setManualNFeForm(current => ({ ...current, destinationDistrict: value }))}
                marginTop='0px'
                marginBottom='16px'
                width='calc(70% - 8px)'
                widthMobile='100%'
                maxLength={300}
              />
            </div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <Field
                type={FieldType.Text}
                label={'Cidade/município'}
                value={manualNFeForm.destinationCityName}
                change={(value) => setManualNFeForm(current => ({ ...current, destinationCityName: value }))}
                marginTop='0px'
                marginBottom='16px'
                width='calc(70% - 8px)'
                widthMobile='100%'
                maxLength={300}
              />
              <Field
                type={FieldType.Select}
                label={'UF'}
                selectProps={{
                  options: federativeUnitOptions,
                  displayField: 'abbreviation',
                  keyField: 'abbreviation'
                }}
                value={manualNFeForm.destinationState}
                change={(value) => setManualNFeForm(current => ({ ...current, destinationState: value }))}
                marginTop='0px'
                marginBottom='16px'
                width='calc(30% - 8px)'
                widthMobile='100%'
              />
            </div>
            <Field
              type={FieldType.Text}
              label={'Complemento (opcional)'}
              value={manualNFeForm.destinationAdditionalAddress}
              change={(value) => setManualNFeForm(current => ({ ...current, destinationAdditionalAddress: value }))}
              marginTop='0px'
              marginBottom='16px'
              maxLength={300}
            />
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <Field
                type={FieldType.Mask}
                label={'CEP'}
                value={manualNFeForm.destinationZipCode}
                change={(value) => setManualNFeForm(current => ({ ...current, destinationZipCode: `${value || ''}`.replace(/\D/g, '').substring(0, 8) }))}
                marginTop='0px'
                width='calc(30% - 8px)'
                widthMobile='100%'
                maxLength={8}
                mask='00000000'
              />
              <Field
                type={FieldType.Text}
                label={'Documento do destinatário'}
                textMuted={'Informe CPF ou CNPJ.'}
                value={manualNFeForm.destinationDocumentNumber}
                change={(value) => setManualNFeForm(current => ({ ...current, destinationDocumentNumber: value }))}
                marginTop='0px'
                width='calc(70% - 8px)'
                widthMobile='100%'
                maxLength={14}
              />
            </div>
          </div>
        </>
      );
    }

    if (currentErpIsProtheus && importXmlViewVisible) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '180px' }}>
          <Button
            text={importingXml ? 'Importando XML...' : 'Selecionar arquivo XML'}
            click={() => openXmlFileDialog()}
            disabled={importingXml}
          />
          <input
            type='file'
            accept='.xml,text/xml,application/xml'
            style={{ display: 'none' }}
            ref={xmlFileInput}
            onChange={changeXmlFile}
          />
        </div>
      );
    }

    if (showImportXmlDetails) {
      return (
        <>
          <div style={{ border: '1px solid #e5e5e5', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '12px 16px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #e5e5e5' }}>
              <div style={{ fontWeight: 600 }}>Nota Fiscal</div>
              {importXmlOutput?.linkToFile ? (
                <a href={importXmlOutput.linkToFile} target='_blank' rel='noreferrer'>
                  Abrir XML
                </a>
              ) : null}
            </div>
            <div style={{ padding: '16px' }}>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <Field
                  type={FieldType.Text}
                  label={'Número'}
                  value={importXmlOutput?.invoice?.identification?.number || ''}
                  width='22%'
                  widthMobile='100%'
                  marginTop='0px'
                  marginBottom='16px'
                  disabled={true}
                />
                <Field
                  type={FieldType.Text}
                  label={'Série'}
                  value={importXmlOutput?.invoice?.identification?.series || ''}
                  width='10%'
                  widthMobile='100%'
                  marginTop='0px'
                  marginBottom='16px'
                  disabled={true}
                />
                <Field
                  type={FieldType.Text}
                  label={'Protocolo'}
                  value={importXmlOutput?.invoice?.authorization?.protocolNumber || ''}
                  width='34%'
                  widthMobile='100%'
                  marginTop='0px'
                  marginBottom='16px'
                  disabled={true}
                />
              </div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <Field
                  type={FieldType.Text}
                  label={'Data'}
                  value={getImportXmlIssuedAtValue(importXmlOutput)}
                  width='30%'
                  widthMobile='100%'
                  marginTop='0px'
                  marginBottom='16px'
                  disabled={true}
                />
                <Field
                  type={FieldType.Text}
                  label={'Total'}
                  value={getImportXmlTotalValue(importXmlOutput)}
                  width='22%'
                  widthMobile='100%'
                  marginTop='0px'
                  marginBottom='16px'
                  disabled={true}
                />
              </div>
              <Field
                type={FieldType.Text}
                label={'Chave de acesso'}
                value={importXmlOutput?.invoice?.identification?.accessKey || ''}
                marginTop='0px'
                disabled={true}
              />
            </div>
          </div>

          <div style={{ border: '1px solid #e5e5e5', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #e5e5e5', fontWeight: 600 }}>
              Destinatário
            </div>
            <div style={{ padding: '16px' }}>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <Field
                  type={FieldType.Text}
                  label={'Nome'}
                  value={importXmlOutput?.destination?.identification?.name || ''}
                  width='calc(65% - 8px)'
                  widthMobile='100%'
                  marginTop='0px'
                  marginBottom='16px'
                  disabled={true}
                />
                <Field
                  type={FieldType.Text}
                  label={'Documento'}
                  value={importXmlOutput?.destination?.identification?.cpf || importXmlOutput?.destination?.identification?.cnpj || ''}
                  width='calc(35% - 8px)'
                  widthMobile='100%'
                  marginTop='0px'
                  marginBottom='16px'
                  disabled={true}
                />
              </div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <Field
                  type={FieldType.Text}
                  label={'CEP'}
                  value={importXmlOutput?.destination?.address?.zipCode || ''}
                  width='16%'
                  widthMobile='100%'
                  marginTop='0px'
                  marginBottom='16px'
                  disabled={true}
                />
                <Field
                  type={FieldType.Text}
                  label={'Cidade'}
                  value={importXmlOutput?.destination?.address?.cityName || ''}
                  width='56%'
                  widthMobile='100%'
                  marginTop='0px'
                  marginBottom='16px'
                  disabled={true}
                />
                <Field
                  type={FieldType.Text}
                  label={'UF'}
                  value={importXmlOutput?.destination?.address?.state || ''}
                  width='12%'
                  widthMobile='100%'
                  marginTop='0px'
                  marginBottom='16px'
                  disabled={true}
                />
              </div>
              <Field
                type={FieldType.Text}
                label={'Logradouro'}
                value={importXmlOutput?.destination?.address?.publicPlace || ''}
                marginTop='0px'
                marginBottom='16px'
                disabled={true}
              />
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <Field
                  type={FieldType.Text}
                  label={'Numero'}
                  value={importXmlOutput?.destination?.address?.number || ''}
                  width='calc(25% - 8px)'
                  widthMobile='100%'
                  marginTop='0px'
                  marginBottom='16px'
                  disabled={true}
                />
                <Field
                  type={FieldType.Text}
                  label={'Bairro'}
                  value={importXmlOutput?.destination?.address?.district || ''}
                  width='calc(75% - 8px)'
                  widthMobile='100%'
                  marginTop='0px'
                  marginBottom='16px'
                  disabled={true}
                />
              </div>
              <Field
                type={FieldType.Text}
                label={'Complemento'}
                value={importXmlOutput?.destination?.address?.additionalAddress || ''}
                marginTop='0px'
                disabled={true}
              />
            </div>
          </div>
        </>
      );
    }

    if (!currentErpIsProtheus && formNFeVisible) {
      return (
        <Field
          type={FieldType.Text}
          label={'Informe o número da nota fiscal (incluir zeros à esquerda, conforme está no Omie)'}
          value={numberNF}
          change={(value) => setNumberNF(value)}
        />
      )
    }

    if (!erpOrder || !erpOrder.erpNfes || erpOrder.erpNfes.length === 0) {
      return (
        <Alert variant='warning' style={{ marginBottom: '0px' }}>
          Nenhuma nota fiscal encontrada.
        </Alert>
      );
    }

    return (
      <div style={{ paddingBottom: '120px' }}>
        {erpOrder.erpNfes.map((nfe, index) => {
      const nfeHistory = history.filter(h => {
        const searchValue = 'NFs depois:';
        let startPosition = h.details.indexOf(searchValue);
        if (startPosition < 0) return false;
        startPosition += searchValue.length;
        const nfs = h.details.substring(startPosition)?.trim()?.split(',');
        return nfs?.length > 0 && nfs.some(nf => nf.trim() == nfe.nfeNumber);
      });
      const firstNfeHistory = nfeHistory.length > 0 ? nfeHistory[0] : null;
      return (
        <ListItem borderTop={index > 0} isFirst={index === 0} isLast={index === erpOrder.erpNfes.length - 1} key={`item-${index}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
            <LeftContainer style={{ flex: '1 1 45%', minWidth: '260px' }}>
              <Title>{nfe.nfeNumber}</Title>
              <Details>{nfe.keyNfe}</Details>
            </LeftContainer>
            <div style={{ flex: '0 1 220px', minWidth: '180px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <CreatedAt>{nfe.issuedAtDate + ' ' + nfe.issuedAtTime}</CreatedAt>
              {nfe.isCancelled ? <Status>{'Cancelada'}</Status> : <div style={{ minHeight: '22px' }}></div>}
            </div>
            <div style={{ flex: '0 0 auto', minWidth: '90px', display: 'flex', justifyContent: 'flex-end' }}>
              {getNFeActionItems(nfe).length > 0 ? (
                <DropdownButton
                  items={getNFeActionItems(nfe)}
                  defaultStyle={true}
                  small={true}
                  text={'Ações'}
                  buttonColor={DropdownButtonColor.Secondary}
                  direction={DropdownButtonDirection.DropToDown}
                  alignment={DropdownButtonAlignment.End}
                  clickItem={(key) => handleNFeAction(key, nfe)}
                />
              ) : null}
            </div>
          </div>
          {firstNfeHistory ? (
            <HistoryBlock>
              Adicionada <strong>{moment(firstNfeHistory.createdAt).format('DD/MM/yyyy HH:mm:ss')}</strong> após localização e 
              alteração por <strong>{firstNfeHistory.userName}</strong>.
            </HistoryBlock>
          ) : null}
        </ListItem>
          )
        })}
      </div>
    );
  }, [visible, erpOrder, formNFeVisible, numberNF, loading, history, currentErpIsProtheus, manualNFeFormVisible, importXmlViewVisible, importXmlConfirmationVisible, importedNFeViewingVisible, manualNFeForm, importingXml, importXmlOutput]);

  const memoFooter = React.useMemo(() => {
    if (!visible || loading) return null;

    if (!canAccessNFeActions) return null;

    let buttons = null;

    if (currentErpIsProtheus && manualNFeFormVisible) {
      buttons = (
        <>
          <Button text={'Cancelar'} marginRight='20px' click={() => cancelManualNFeForm()} />
          <Button text={'Salvar'} click={() => saveManualNFe()} />
        </>
      );
    }

    if (currentErpIsProtheus && importXmlViewVisible) {
      buttons = (
        <>
          <Button text={'Voltar'} click={() => cancelImportXmlView()} disabled={importingXml} />
        </>
      );
    }

    if (currentErpIsProtheus && importXmlConfirmationVisible) {
      buttons = (
        <>
          <Button text={'Voltar'} marginRight='20px' click={() => backToImportXmlView()} disabled={confirmingImportedXml} />
          <Button text={confirmingImportedXml ? 'Confirmando...' : 'Confirmar Importação'} click={() => confirmImportXml()} disabled={confirmingImportedXml} />
        </>
      );
    }

    if (currentErpIsProtheus && importedNFeViewingVisible) {
      buttons = (
        <>
          <Button text={'Voltar'} click={() => cancelImportedNFeView()} />
        </>
      );
    }

    if (!currentErpIsProtheus && formNFeVisible) {
      buttons = (
        <>
          <Button text={'Cancelar'} marginRight='20px' click={() => cancelFormNFe()} />
          <Button text={'Confirmar'} click={() => getNFe()} />
        </>
      );
    }

    if (currentErpIsProtheus && !buttons) {
      buttons = (
        <>
          {isAdmin ? <Button text={'Incluir NFe'} marginRight='20px' click={() => startManualNFeForm()} /> : null}
          <Button text={'Importar NFe/XML'} click={() => startImportXmlView()} />
        </>
      );
    }

    if (!currentErpIsProtheus && !buttons) {
      buttons = (
        <>
          <Button text={'Atualizar ou obter nota fiscal'} click={() => startFormNFe()} />
        </>
      );
    }

    if (buttons) {
      return (
        <Modal.Footer>
          <FooterContent>
            {buttons}
          </FooterContent>
        </Modal.Footer>
      );
    }

    return null;
  }, [visible, erpOrder, formNFeVisible, numberNF, loading, history, currentErpIsProtheus, manualNFeFormVisible, importXmlViewVisible, importXmlConfirmationVisible, importedNFeViewingVisible, manualNFeForm, canAccessNFeActions, isAdmin, importingXml, confirmingImportedXml]);

  if (!visible) return null;

  return (
    <Modal
      show={visible}
      onHide={() => clickCloseModal()}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body id='modalListNFesBody' style={{ maxHeight: '78vh', overflowY: 'auto' }}>
        {body}
      </Modal.Body>

      {memoFooter}
    </Modal>
  );

  function clickCloseModal() {
    resetViews();
    setManualNFeFormVisible(false);
    close(orderId);
  }

  function startFormNFe() {
    resetViews();
    setNumberNF('');
    setFormNFeVisible(true);
  }

  function cancelFormNFe() {
    setNumberNF('');
    setFormNFeVisible(false);
  }

  async function startManualNFeForm(nfe?: ErpNfe) {
    resetViews();

    if (nfe) {
      setEditingManualNFe(nfe);
      setManualNFeForm({
        nfeNumber: nfe.nfeNumber || '',
        series: nfe.nfeSeries || '',
        totalValue: erpOrder?.totalValue !== undefined && erpOrder?.totalValue !== null ? `${erpOrder.totalValue}` : '',
        keyNfe: nfe.keyNfe || '',
        authorizationProtocol: nfe.protocolNumber !== undefined && nfe.protocolNumber !== null ? `${nfe.protocolNumber}` : '',
        issuedAt: getIssuedAtDate(nfe),
        destinationPublicPlace: nfe.nfeDestinationAddressList?.[0]?.publicPlace || '',
        destinationNumber: nfe.nfeDestinationAddressList?.[0]?.number || '',
        destinationDistrict: nfe.nfeDestinationAddressList?.[0]?.district || '',
        destinationCityName: nfe.nfeDestinationAddressList?.[0]?.cityName || '',
        destinationState: nfe.nfeDestinationAddressList?.[0]?.state || '',
        destinationZipCode: nfe.nfeDestinationAddressList?.[0]?.zipCode || '',
        destinationAdditionalAddress: nfe.nfeDestinationAddressList?.[0]?.additionalAddress || '',
        destinationDocumentNumber: nfe.nfeDestinationAddressList?.[0]?.documentNumber || '',
      });
    } else {
      const currentShippingAddress = await getShippingAddress();
      setEditingManualNFe(null);
      const nextForm = getInitialManualNFeFormByShippingAddress(currentShippingAddress);
      setManualNFeForm(nextForm);
    }

    setManualNFeFormVisible(true);
  }

  function cancelManualNFeForm() {
    setEditingManualNFe(null);
    setManualNFeForm(getInitialManualNFeFormByShippingAddress());
    setManualNFeFormVisible(false);
  }

  function startImportXmlView() {
    resetViews();
    setImportXmlViewVisible(true);
  }

  function cancelImportXmlView() {
    setImportXmlViewVisible(false);
    setImportXmlConfirmationVisible(false);
    setImportedNFeViewingVisible(false);
    setImportXmlOutput(null);
    setConfirmingImportedXml(false);
    resetXmlFileInput();
  }

  function backToImportXmlView() {
    setImportXmlConfirmationVisible(false);
    setImportedNFeViewingVisible(false);
    setImportXmlViewVisible(true);
    setConfirmingImportedXml(false);
  }

  function cancelImportedNFeView() {
    setImportedNFeViewingVisible(false);
    setImportXmlOutput(null);
  }

  function getInitialManualNFeFormByShippingAddress(currentShippingAddress?: ShippingAddress | null) {
    const currentAddress = currentShippingAddress === undefined ? shippingAddress : currentShippingAddress;
    const shippingDocumentNumber = `${currentAddress?.company || ''}`.trim();

    return {
      ...initialManualNFeForm,
      totalValue: erpOrder?.totalValue !== undefined && erpOrder?.totalValue !== null ? `${erpOrder.totalValue}` : '',
      destinationPublicPlace: `${currentAddress?.address || ''}`.trim(),
      destinationCityName: `${currentAddress?.city || ''}`.trim(),
      destinationState: `${currentAddress?.provinceCode || ''}`.trim(),
      destinationZipCode: `${currentAddress?.zip || ''}`.replace(/\D/g, '').substring(0, 8),
      destinationAdditionalAddress: `${currentAddress?.additionalAddress || ''}`.trim(),
      destinationDocumentNumber: shippingDocumentNumber,
    };
  }

  function getErpOrder() {
    if (!orderId) return;
    setLoading(true);
    getUpdatedErpOrderService
      .handle({
        orderId: orderId,
        userId: session?.user?.id
      }).then(result => {
        setErpOrder(result?.value?.erpOrder);
        getHistoryByOrderService.handle({ orderId: orderId })
          .then(hresult => {
            const nfeHistory = (hresult?.value?.history || []).filter(h => h.stateDescription.includes('Localização e alteração de NFe por Usuário'));
            setHistory(nfeHistory);
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {
        setLoading(false);
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      });
  }

  function extractShippingAddress(result: any): ShippingAddress | null {
    const directValue = result?.value;

    if (directValue?.address || directValue?.city || directValue?.zip || directValue?.provinceCode || directValue?.company) {
      return directValue as ShippingAddress;
    }

    const nestedValue = directValue?.shippingAddress || result?.shippingAddress || result?.value?.value;

    if (nestedValue?.address || nestedValue?.city || nestedValue?.zip || nestedValue?.provinceCode || nestedValue?.company) {
      return nestedValue as ShippingAddress;
    }

    return null;
  }

  async function getShippingAddress() {
    if (!orderId) return null;

    try {
      const result = await getShippingAddressByOrderService.handle({ orderId: orderId });
      let currentShippingAddress = extractShippingAddress(result);

      if (!currentShippingAddress) {
        const addressesResult = await getOrderAddressesService.handle({ orderId: orderId });
        currentShippingAddress = addressesResult?.value?.shippingAddresses?.[0] || null;
      }

      setShippingAddress(currentShippingAddress);
      return currentShippingAddress;
    } catch {
      try {
        const addressesResult = await getOrderAddressesService.handle({ orderId: orderId });
        const currentShippingAddress = addressesResult?.value?.shippingAddresses?.[0] || null;
        setShippingAddress(currentShippingAddress);
        return currentShippingAddress;
      } catch {
        setShippingAddress(null);
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
        return null;
      }
    }
  }

  async function getNFe() {
    if (!orderId) return;

    if (!numberNF) {
      toastService.warning('Informe o número da nota fiscal');
      return;
    }

    const getNfResult = await getErpNfService.handle({ number: numberNF });

    const erpOrderId = getNfResult?.value?.nf?.complement?.orderId;
    const erpNfeKey = getNfResult?.value?.nf?.complement?.nfeKey;
    const erpNfValue = getNfResult?.value?.nf?.total?.icmsTotal?.nfValue;

    if (!erpOrderId || !erpNfeKey || erpNfValue === undefined || erpNfValue === null) {
      toastService.warning('Nota fiscal não encontrada.');
      return;
    }

    const message = `Nota fiscal encontrada: <br>Chave: ${erpNfeKey} <br>Valor: ${erpNfValue.toFixed(2)} <br>Confirma a nota fiscal?`;
    const confirmResult = await alertService.confirmHtml(message);

    if (!confirmResult.isConfirmed) {
      cancelFormNFe();
      return;
    }

    const input = new GetErpOrderNFeInput();
    input.orderId = orderId;
    input.erpOrderCode = erpOrderId;
    input.collectOrder = false;
    input.userId = session?.user?.id;
    const getErpOrderResult = await getErpOrderNFeService.handle(input);

    if (!getErpOrderResult?.value?.result) {
      alertService.warning('Não foi possível obter e salvar ou atualizar a nota fiscal. Aguarde alguns segundos, tente novamente e se o problema persistir após várias tentativas, avise o time Tech.');
      return;
    }

    toastService.info('Nota fiscal salva ou atualizada com sucesso.');

    cancelFormNFe();
    getErpOrder();
  }

  async function saveManualNFe() {
    const {
      nfeNumber,
      series,
      totalValue,
      keyNfe,
      authorizationProtocol,
      issuedAt,
      destinationPublicPlace,
      destinationNumber,
      destinationDistrict,
      destinationCityName,
      destinationState,
      destinationZipCode,
      destinationAdditionalAddress,
      destinationDocumentNumber,
    } = manualNFeForm;

    if (!nfeNumber || !series || !totalValue || !keyNfe || !authorizationProtocol || !issuedAt) {
      toastService.warning('Informe todos os dados da NFe.');
      return;
    }

    if (!destinationPublicPlace || !destinationNumber || !destinationDistrict || !destinationCityName
      || !destinationState || !destinationZipCode || !destinationDocumentNumber) {
      toastService.warning('Informe os dados obrigatórios do endereço de entrega.');
      return;
    }

    if (!orderId) return;

    const parsedTotalValue = Number(totalValue);
    const parsedProtocolNumber = Number(authorizationProtocol);

    if (!Number.isFinite(parsedTotalValue)) {
      toastService.warning('Informe um total final válido para a NFe.');
      return;
    }

    if (parsedTotalValue <= 0) {
      toastService.warning('Informe um total final maior que zero para a NFe.');
      return;
    }

    if (!Number.isInteger(parsedProtocolNumber) || parsedProtocolNumber <= 0) {
      toastService.warning('Informe um protocolo de autorização válido.');
      return;
    }

    if (parsedTotalValue > 5000) {
      const confirmHighTotal = await alertService.confirm(`O total da NFe informado foi ${parsedTotalValue.toFixed(2)}. Confirma esse valor?`);

      if (!confirmHighTotal.isConfirmed) {
        return;
      }
    }

    const destinationDocumentDigits = destinationDocumentNumber.replace(/\D/g, '');

    if (!destinationDocumentDigits) {
      toastService.warning('Informe um documento válido para o destinatário.');
      return;
    }

    const input = new SaveErpOrderNFeInput();
    input.orderId = orderId;
    input.erpNfeId = editingManualNFe?.id ? editingManualNFe.id : undefined;
    input.nfeNumber = nfeNumber;
    input.nfeSeries = series;
    input.totalFinalValue = parsedTotalValue;
    input.keyNfe = keyNfe;
    input.protocolNumber = parsedProtocolNumber;
    input.issuedAt = issuedAt;
    const destinationAddress = new SaveErpOrderNFeDestinationAddressInput();
    destinationAddress.publicPlace = destinationPublicPlace;
    destinationAddress.number = destinationNumber;
    destinationAddress.district = destinationDistrict;
    destinationAddress.cityName = destinationCityName;
    destinationAddress.state = destinationState;
    destinationAddress.zipCode = destinationZipCode;
    destinationAddress.additionalAddress = destinationAdditionalAddress;
    destinationAddress.cpf = destinationDocumentDigits.length > 11 ? undefined : destinationDocumentDigits;
    destinationAddress.cnpj = destinationDocumentDigits.length > 11 ? destinationDocumentDigits : undefined;
    input.nfeDestinationAddress = destinationAddress;
    input.userId = session?.user?.id;

    const result = await saveErpOrderNFeService.handle(input);

    if (!result?.value?.result) {
      alertService.warning('Não foi possível salvar a nota fiscal. Tente novamente e, se o problema persistir, avise o time Tech.');
      return;
    }

    toastService.info(editingManualNFe ? 'Nota fiscal atualizada com sucesso.' : 'Nota fiscal salva com sucesso.');

    cancelManualNFeForm();
    getErpOrder();
  }

  function getIssuedAtDate(nfe: ErpNfe): Date | null {
    if (!nfe.issuedAtDate) return null;

    const fullDate = `${nfe.issuedAtDate}${nfe.issuedAtTime ? ` ${nfe.issuedAtTime}` : ''}`;
    const parsedDate = moment(fullDate, ['DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm', 'DD/MM/YYYY', moment.ISO_8601], true);

    return parsedDate.isValid() ? parsedDate.toDate() : null;
  }

  function canEditManualNFe(nfe: ErpNfe): boolean {
    if (!currentErpIsProtheus || !isAdmin || (nfe.createdById || 0) <= 0) return false;
    if (isImportedNFe(nfe)) return false;
    if (nfe.isCancelled) return false;
    return true;
  }

  function canDeleteManualNFe(nfe: ErpNfe): boolean {
    return currentErpIsProtheus && isAdmin && !nfe.isCancelled && ((nfe.createdById || 0) > 0 || isImportedNFe(nfe));
  }

  function canViewImportedNFe(nfe: ErpNfe): boolean {
    return currentErpIsProtheus && canAccessNFeActions && isImportedNFe(nfe);
  }

  function getNFeActionItems(nfe: ErpNfe): IDropdownButtonItem[] {
    const items: IDropdownButtonItem[] = [];

    if (canViewImportedNFe(nfe)) {
      items.push({ key: nfeActionKey.View, text: 'Visualizar' });
    }

    if (canEditManualNFe(nfe)) {
      items.push({ key: nfeActionKey.Edit, text: 'Editar' });
    }

    if (canCancelNFe(nfe)) {
      items.push({ key: nfeActionKey.Cancel, text: 'Cancelar' });
    }

    if (canDeleteManualNFe(nfe)) {
      items.push({ key: nfeActionKey.Delete, text: 'Excluir' });
    }

    return items;
  }

  function handleNFeAction(key: any, nfe: ErpNfe) {
    if (key === nfeActionKey.View) {
      startImportedNFeView(nfe);
      return;
    }

    if (key === nfeActionKey.Edit) {
      startManualNFeForm(nfe);
      return;
    }

    if (key === nfeActionKey.Cancel) {
      cancelNFe(nfe);
      return;
    }

    if (key === nfeActionKey.Delete) {
      deleteManualNFe(nfe);
    }
  }

  function canCancelNFe(nfe: ErpNfe): boolean {
    return canAccessNFeActions
      && !nfe.isCancelled
      && `${nfe.erpCode || ''}`.toLowerCase() === zerezesErpCode.Protheus;
  }

  function openXmlFileDialog() {
    resetXmlFileInput();
    xmlFileInput.current?.click();
  }

  async function changeXmlFile(event: React.ChangeEvent<HTMLInputElement>) {
    event.stopPropagation();
    event.preventDefault();

    const file = event.target.files?.[0];

    if (!file || !orderId) {
      resetXmlFileInput();
      return;
    }

    if (!file.name.toLowerCase().endsWith('.xml')) {
      toastService.warning('Selecione um arquivo XML válido.');
      resetXmlFileInput();
      return;
    }

    const formData = new FormData();
    formData.append('file', file, file.name);

    const input = new ImportErpOrderNFeXmlInput();
    input.orderId = orderId;
    input.formData = formData;

    try {
      setImportingXml(true);
      const result = await importErpOrderNFeXmlService.handle(input);

      if (!result?.value?.result) {
        toastService.warning('Não foi possível importar o XML da NFe.');
        return;
      }

      setImportXmlOutput(result.value);
      setImportXmlViewVisible(false);
      setImportXmlConfirmationVisible(true);
    } catch {
      toastService.warning(strings.OpsSomethingWrongSendTheData);
    } finally {
      setImportingXml(false);
      resetXmlFileInput();
    }
  }

  async function deleteManualNFe(nfe: ErpNfe) {
    if (!orderId || !nfe.id) return;

    const confirmation = await alertService.confirm(`Deseja realmente excluir a NFe ${nfe.nfeNumber}?`);

    if (!confirmation.isConfirmed) return;

    const result = await deleteErpOrderNFeService.handle({
      orderId: orderId,
      erpNfeId: nfe.id,
      userId: session?.user?.id,
    });

    if (!result?.value?.result) {
      alertService.warning('Não foi possível excluir a nota fiscal. Tente novamente e, se o problema persistir, avise o time Tech.');
      return;
    }

    toastService.info('Nota fiscal excluída com sucesso.');
    getErpOrder();
  }

  async function cancelNFe(nfe: ErpNfe) {
    if (!nfe.id) return;

    const confirmation = await alertService.confirm(`Deseja realmente cancelar a NFe ${nfe.nfeNumber}?`);

    if (!confirmation.isConfirmed) return;

    const result = await cancelErpNfeService.handle({
      erpNfeId: nfe.id,
      userId: session?.user?.id,
    });

    if (!result?.value?.result) {
      alertService.warning('Não foi possível cancelar a nota fiscal. Tente novamente e, se o problema persistir, avise o time Tech.');
      return;
    }

    toastService.info('Nota fiscal cancelada com sucesso.');
    getErpOrder();
  }

  function getCurrentErpCodeFromAppConfig() {
    featureFlagService.currentErpIsProtheus()
      .then(result => {
        setCurrentErpIsProtheus(result);
      })
      .catch(() => {
        toastService.warning(strings.OpsSomethingWrongGettingTheData);
      });
  }

  function resetViews() {
    setFormNFeVisible(false);
    setManualNFeFormVisible(false);
    setImportXmlViewVisible(false);
    setImportXmlConfirmationVisible(false);
    setImportedNFeViewingVisible(false);
    setConfirmingImportedXml(false);
  }

  function resetXmlFileInput() {
    if (xmlFileInput.current) {
      xmlFileInput.current.value = '';
    }
  }

  async function confirmImportXml() {
    if (!orderId || !importXmlOutput) return;

    const nfeNumber = importXmlOutput.invoice?.identification?.number;
    const nfeSeries = importXmlOutput.invoice?.identification?.series;
    const keyNfe = importXmlOutput.invoice?.identification?.accessKey;
    const protocolNumber = importXmlOutput.invoice?.authorization?.protocolNumber;
    const issuedAt = importXmlOutput.invoice?.issuedAt;
    const totalValue = importXmlOutput.invoice?.totalValue;
    const xmlLink = importXmlOutput.linkToFile;
    const destinationAddress = importXmlOutput.destination?.address;
    const identification = importXmlOutput.destination?.identification;

    if (!nfeNumber || !nfeSeries || !keyNfe || !protocolNumber || !issuedAt || totalValue === undefined || totalValue === null || !xmlLink) {
      toastService.warning('Não foi possível confirmar a importação da NFe. Revise os dados retornados pelo XML.');
      return;
    }

    const parsedProtocolNumber = Number(protocolNumber);

    if (!Number.isFinite(parsedProtocolNumber) || !Number.isInteger(parsedProtocolNumber) || parsedProtocolNumber <= 0) {
      toastService.warning('Não foi possível confirmar a importação da NFe. O protocolo retornado é inválido.');
      return;
    }

    const input = new ConfirmImportedErpOrderNFeInput();
    input.orderId = orderId;
    input.nfeNumber = nfeNumber;
    input.nfeSeries = nfeSeries;
    input.totalFinalValue = totalValue;
    input.keyNfe = keyNfe;
    input.protocolNumber = parsedProtocolNumber;
    input.issuedAt = getImportXmlIssuedAtInputValue(issuedAt);
    input.xmlLink = xmlLink;
    input.userId = session?.user?.id;

    if (destinationAddress) {
      const mappedDestinationAddress = new SaveErpOrderNFeDestinationAddressInput();
      mappedDestinationAddress.name = identification?.name || undefined;
      mappedDestinationAddress.publicPlace = destinationAddress.publicPlace || '';
      mappedDestinationAddress.number = destinationAddress.number || '';
      mappedDestinationAddress.district = destinationAddress.district || '';
      mappedDestinationAddress.cityName = destinationAddress.cityName || '';
      mappedDestinationAddress.state = destinationAddress.state || '';
      mappedDestinationAddress.zipCode = destinationAddress.zipCode || '';
      mappedDestinationAddress.additionalAddress = destinationAddress.additionalAddress || '';
      mappedDestinationAddress.cpf = identification?.cpf || undefined;
      mappedDestinationAddress.cnpj = identification?.cnpj || undefined;
      input.nfeDestinationAddress = mappedDestinationAddress;
    }

    try {
      setConfirmingImportedXml(true);
      const result = await confirmImportedErpOrderNFeService.handle(input);

      if (!result?.value?.result) {
        if (result?.value?.alreadyExists) {
          alertService.warning('Essa nota fiscal já foi importada para este pedido.');
          return;
        }

        alertService.warning('Falha ao confirmar a importação da nota fiscal.');
        return;
      }

      cancelImportXmlView();
      getErpOrder();
    } catch {
      alertService.warning('Falha ao confirmar a importação da nota fiscal.');
    } finally {
      setConfirmingImportedXml(false);
    }
  }

  function isImportedNFe(nfe: ErpNfe) {
    return (nfe.importedById || 0) > 0;
  }

  function startImportedNFeView(nfe: ErpNfe) {
    resetViews();
    setImportXmlOutput(mapImportedNFeToImportXmlOutput(nfe));
    setImportedNFeViewingVisible(true);
  }

  function mapImportedNFeToImportXmlOutput(nfe: ErpNfe): ImportErpOrderNFeXmlOutput {
    const destinationAddress = nfe.nfeDestinationAddressList?.[0];
    const documentNumber = `${destinationAddress?.documentNumber || ''}`.replace(/\D/g, '');
    const cpf = documentNumber && documentNumber.length <= 11 ? documentNumber : null;
    const cnpj = documentNumber && documentNumber.length > 11 ? documentNumber : null;

    return {
      result: true,
      linkToFile: nfe.xmlLink || null,
      destination: destinationAddress ? {
        identification: {
          name: destinationAddress.destinationName || null,
          cpf: cpf,
          cnpj: cnpj,
        },
        address: {
          publicPlace: destinationAddress.publicPlace || null,
          number: destinationAddress.number || null,
          additionalAddress: destinationAddress.additionalAddress || null,
          district: destinationAddress.district || null,
          cityName: destinationAddress.cityName || null,
          state: destinationAddress.state || null,
          zipCode: destinationAddress.zipCode || null,
        }
      } : null,
      invoice: {
        identification: {
          accessKey: nfe.keyNfe || null,
          number: nfe.nfeNumber || null,
          series: nfe.nfeSeries || null,
        },
        authorization: {
          protocolNumber: nfe.protocolNumber !== undefined && nfe.protocolNumber !== null ? `${nfe.protocolNumber}` : null,
        },
        issuedAt: getIssuedAtDate(nfe),
        totalValue: nfe.totalFinalValue ?? null,
      }
    };
  }

  function getImportXmlIssuedAtValue(output: ImportErpOrderNFeXmlOutput | null) {
    const issuedAt = output?.invoice?.issuedAt;

    if (!issuedAt) return '';

    const parsedDate = moment(issuedAt);

    if (!parsedDate.isValid()) return '';

    return parsedDate.format('DD/MM/YYYY HH:mm:ss');
  }

  function getImportXmlTotalValue(output: ImportErpOrderNFeXmlOutput | null) {
    const totalValue = output?.invoice?.totalValue;

    if (totalValue === undefined || totalValue === null) return '';

    return `${totalValue}`;
  }

  function getImportXmlIssuedAtInputValue(issuedAt: Date | string) {
    if (typeof issuedAt === 'string') {
      const parsedDate = moment.parseZone(issuedAt);

      if (parsedDate.isValid()) {
        return parsedDate.format('YYYY-MM-DDTHH:mm:ss');
      }

      return issuedAt;
    }

    const parsedDate = moment(issuedAt);

    if (!parsedDate.isValid()) {
      return '';
    }

    return parsedDate.format('YYYY-MM-DDTHH:mm:ss');
  }
}

export { ModalListNFes };
